import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { stringify as stringifyYAML } from 'yaml';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Info, 
  AlertCircle, 
  DollarSign,
  Database,
  TrendingUp,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  Target,
  Shield,
  BarChart3,
  Zap,
  ChevronDown,
  Save,
  FileText,
  Code,
  Download,
  RefreshCw
} from 'lucide-react';
import { scriptsApi } from '@/services/api/scripts';
import { datasetsApi } from '@/services/api/datasets';
import { settingsTemplatesApi } from '@/services/api/settings-templates';
import { showErrorToast, showSuccessToast } from '@/utils/error-handler';
import { extractSignalNames } from '@/utils/dsl-helpers';
import type { BacktestConfigForm, StrategyMetadata } from '@/types/backtest';
import { timeframeToSeconds } from '@/types/backtest';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useScriptStore } from '@/store/scriptStore';
import type { SettingsTemplate } from '@/types/settings-template';
import { SettingsTemplateType } from '@/types/settings-template';

interface BacktestConfigModalProps {
  scriptId: string;
  dslCode: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigSaved?: (config: BacktestConfigForm) => void;
}

export default function BacktestConfigModal({
  scriptId,
  dslCode,
  open,
  onOpenChange,
  onConfigSaved,
}: BacktestConfigModalProps) {
  const { t } = useTranslation();
  const { currentScript } = useScriptStore();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'signals' | 'advanced'>('basic');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Form state - Basic
  const [initialCapital, setInitialCapital] = useState<number>(10000);
  const [signalDatasetId, setSignalDatasetId] = useState<string>('');
  const [useSeparateExecutionDataset, setUseSeparateExecutionDataset] = useState<boolean>(false);
  const [executionDatasetId, setExecutionDatasetId] = useState<string>('');
  const [closeEndOfDay, setCloseEndOfDay] = useState<boolean>(false);
  const [leverage, setLeverage] = useState<number | undefined>(undefined);
  const [marginRequirement, setMarginRequirement] = useState<number | undefined>(undefined);

  // Load datasets
  const { data: datasetsData } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const result = await datasetsApi.list()
      return result.datasets
    },
  });
  
  const readyDatasets = datasetsData?.filter((d) => d.status === 'ready') || [];
  
  // Get selected datasets info
  const signalDataset = readyDatasets.find(d => d.id === signalDatasetId);
  const executionDataset = readyDatasets.find(d => d.id === executionDatasetId);
  // Position Size / Capital Management
  const [positionSizeType, setPositionSizeType] = useState<'fixed' | 'percentage' | 'risk_per_trade'>('fixed');
  const [positionSize, setPositionSize] = useState<number>(1);
  const [maxRiskPerTrade, setMaxRiskPerTrade] = useState<number>(2.0); // Percentage for risk_per_trade
  
  // Form state - Signals
  const [availableSignals, setAvailableSignals] = useState<string[]>([]);
  const [entryLongSignals, setEntryLongSignals] = useState<string[]>([]);
  const [entryShortSignals, setEntryShortSignals] = useState<string[]>([]);
  const [exitLongSignals, setExitLongSignals] = useState<string[]>([]);
  const [exitShortSignals, setExitShortSignals] = useState<string[]>([]);
  
  // Form state - SL/TP (separate for Long and Short)
  // Stop Loss - Long
  const [stopLossLongUseSignal, setStopLossLongUseSignal] = useState<boolean>(false);
  const [stopLossLongSignalName, setStopLossLongSignalName] = useState<string>('');
  const [stopLossLongMultiplier, setStopLossLongMultiplier] = useState<number>(2.0);
  const [stopLossLongBasedOn, setStopLossLongBasedOn] = useState<'atr' | 'price' | 'percent'>('atr');
  
  // Stop Loss - Short
  const [stopLossShortUseSignal, setStopLossShortUseSignal] = useState<boolean>(false);
  const [stopLossShortSignalName, setStopLossShortSignalName] = useState<string>('');
  const [stopLossShortMultiplier, setStopLossShortMultiplier] = useState<number>(2.0);
  const [stopLossShortBasedOn, setStopLossShortBasedOn] = useState<'atr' | 'price' | 'percent'>('atr');
  
  // Take Profit - Long
  const [takeProfitLongUseSignal, setTakeProfitLongUseSignal] = useState<boolean>(false);
  const [takeProfitLongSignalName, setTakeProfitLongSignalName] = useState<string>('');
  const [takeProfitLongMultiplier, setTakeProfitLongMultiplier] = useState<number>(3.0);
  const [takeProfitLongBasedOn, setTakeProfitLongBasedOn] = useState<'atr' | 'price' | 'percent'>('atr');
  
  // Take Profit - Short
  const [takeProfitShortUseSignal, setTakeProfitShortUseSignal] = useState<boolean>(false);
  const [takeProfitShortSignalName, setTakeProfitShortSignalName] = useState<string>('');
  const [takeProfitShortMultiplier, setTakeProfitShortMultiplier] = useState<number>(3.0);
  const [takeProfitShortBasedOn, setTakeProfitShortBasedOn] = useState<'atr' | 'price' | 'percent'>('atr');
  
  // Form state - Position Management
  const [maxPositions, setMaxPositions] = useState<number>(1);
  const [allowMultipleEntries, setAllowMultipleEntries] = useState<boolean>(false);
  const [closeOnOppositeSignal, setCloseOnOppositeSignal] = useState<boolean>(true);

  // Integrated config state (from config: block in DSL)
  const [integratedConfig, setIntegratedConfig] = useState<any>(null);
  const [configWarnings, setConfigWarnings] = useState<string[]>([]);
  const [configErrors, setConfigErrors] = useState<Array<{field: string; message: string; code: string}>>([]);
  const [isLoadingIntegratedConfig, setIsLoadingIntegratedConfig] = useState(false);
  const [hasLoadedIntegratedConfig, setHasLoadedIntegratedConfig] = useState(false);

  // Settings Templates state
  const [templates, setTemplates] = useState<SettingsTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  
  // Save as Template dialog state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateAction, setTemplateAction] = useState<'create' | 'update'>('create');
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [selectedTemplateToUpdate, setSelectedTemplateToUpdate] = useState<string>('');

  // Load settings templates
  useEffect(() => {
    if (open) {
      setIsLoadingTemplates(true);
      settingsTemplatesApi.list({ 
        type: SettingsTemplateType.BACKTEST_CONFIG,
        scriptId: scriptId 
      })
        .then((templates) => {
          // Ensure templates is always an array
          setTemplates(Array.isArray(templates) ? templates : []);
        })
        .catch((error) => {
          console.error('Failed to load templates:', error);
          setTemplates([]);
        })
        .finally(() => {
          setIsLoadingTemplates(false);
        });
    } else {
      setTemplates([]);
      setSelectedTemplateId('');
    }
  }, [open, scriptId]);

  // Detect and load integrated config from DSL (only when modal opens, not on every dslCode change)
  useEffect(() => {
    if (open && dslCode) {
      // Only compile once when modal opens to avoid rate limiting (429 errors)
      setIsLoadingIntegratedConfig(true);
      scriptsApi.compileCode(dslCode)
        .then((result) => {
          if (result.integratedConfig) {
            setIntegratedConfig(result.integratedConfig);
          } else {
            setIntegratedConfig(null);
          }
          setConfigWarnings(result.configWarnings || []);
          setConfigErrors(result.configErrors || []);
        })
        .catch((error: any) => {
          // Handle rate limiting gracefully
          if (error?.response?.status === 429) {
            console.warn('Rate limited when extracting integrated config. Skipping...');
            setIntegratedConfig(null);
            setConfigWarnings([]);
            setConfigErrors([]);
          } else {
            console.error('Failed to extract integrated config:', error);
            setIntegratedConfig(null);
            setConfigWarnings([]);
            setConfigErrors([]);
          }
        })
        .finally(() => {
          setIsLoadingIntegratedConfig(false);
        });
    } else {
      setIntegratedConfig(null);
      setConfigWarnings([]);
      setConfigErrors([]);
    }
    // Only depend on 'open' to avoid recompiling on every dslCode change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Detect available signals from DSL code
  useEffect(() => {
    if (open && dslCode) {
      const signals = extractSignalNames(dslCode);
      setAvailableSignals(signals);
      
      // Auto-detect common signal names
      const commonEntrySignals = signals.filter(s => 
        ['entry', 'buy', 'long_entry', 'enter'].includes(s.toLowerCase())
      );
      const commonExitSignals = signals.filter(s => 
        ['exit', 'sell', 'long_exit', 'close'].includes(s.toLowerCase())
      );
      
      if (commonEntrySignals.length > 0 && entryLongSignals.length === 0) {
        setEntryLongSignals([commonEntrySignals[0]]);
      }
      if (commonExitSignals.length > 0 && exitLongSignals.length === 0) {
        setExitLongSignals([commonExitSignals[0]]);
      }
      
      // Auto-detect SL/TP signal names (apply to both long and short)
      const slSignals = signals.filter(s => s.toLowerCase().includes('stop') || s.toLowerCase().includes('sl'));
      const tpSignals = signals.filter(s => s.toLowerCase().includes('target') || s.toLowerCase().includes('tp') || s.toLowerCase().includes('profit'));
      
      if (slSignals.length > 0) {
        if (!stopLossLongUseSignal) {
          setStopLossLongUseSignal(true);
          setStopLossLongSignalName(slSignals[0]);
        }
        if (!stopLossShortUseSignal) {
          setStopLossShortUseSignal(true);
          setStopLossShortSignalName(slSignals[0]);
        }
      }
      if (tpSignals.length > 0) {
        if (!takeProfitLongUseSignal) {
          setTakeProfitLongUseSignal(true);
          setTakeProfitLongSignalName(tpSignals[0]);
        }
        if (!takeProfitShortUseSignal) {
          setTakeProfitShortUseSignal(true);
          setTakeProfitShortSignalName(tpSignals[0]);
        }
      }
    }
  }, [open, dslCode]);

  // Update signalDatasetId when script's selectedDatasetId changes (always sync with script)
  // This runs first to ensure signalDatasetId is always from the script
  useEffect(() => {
    if (open && currentScript?.selectedDatasetId && readyDatasets.length > 0) {
      const scriptDataset = readyDatasets.find(d => d.id === currentScript.selectedDatasetId);
      if (scriptDataset) {
        setSignalDatasetId(currentScript.selectedDatasetId);
      } else {
        // If script's dataset is not in ready datasets, clear it
        setSignalDatasetId('');
      }
    } else if (open && !currentScript?.selectedDatasetId) {
      // If script has no selected dataset, clear it
      setSignalDatasetId('');
    }
  }, [open, currentScript?.selectedDatasetId, readyDatasets]);

  // Reset state when modal closes without saving
  useEffect(() => {
    if (!open) {
      // Reset all form state when modal closes
      // This ensures that when reopening, we load fresh from custom config
      setIsLoading(false);
      setIsSaving(false);
      setValidationErrors({});
      setActiveTab('basic');
      setHasLoadedIntegratedConfig(false);
    }
  }, [open]);

  // Load selected template config (has priority over integrated config)
  useEffect(() => {
    if (open && selectedTemplateId && readyDatasets.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template && template.config) {
        const config = template.config as any;
        
        // Load basic config from template
        if (config.initialCapital !== undefined) setInitialCapital(config.initialCapital);
        if (config.positionSize !== undefined) {
          setPositionSizeType('fixed');
          setPositionSize(config.positionSize);
        }
        
        // Load signal mappings
        if (config.signals) {
          if (config.signals.entry?.long) setEntryLongSignals(config.signals.entry.long);
          if (config.signals.entry?.short) setEntryShortSignals(config.signals.entry.short);
          if (config.signals.exit?.long) setExitLongSignals(config.signals.exit.long);
          if (config.signals.exit?.short) setExitShortSignals(config.signals.exit.short);
        }
        
        // Load SL/TP config
        if (config.stop_loss) {
          if (config.stop_loss.long) {
            setStopLossLongUseSignal(config.stop_loss.long.use_signal_level || false);
            if (config.stop_loss.long.signal_name) setStopLossLongSignalName(config.stop_loss.long.signal_name);
            if (config.stop_loss.long.default_multiplier) setStopLossLongMultiplier(config.stop_loss.long.default_multiplier);
            if (config.stop_loss.long.default_based_on) setStopLossLongBasedOn(config.stop_loss.long.default_based_on);
          }
          if (config.stop_loss.short) {
            setStopLossShortUseSignal(config.stop_loss.short.use_signal_level || false);
            if (config.stop_loss.short.signal_name) setStopLossShortSignalName(config.stop_loss.short.signal_name);
            if (config.stop_loss.short.default_multiplier) setStopLossShortMultiplier(config.stop_loss.short.default_multiplier);
            if (config.stop_loss.short.default_based_on) setStopLossShortBasedOn(config.stop_loss.short.default_based_on);
          }
        }
        
        if (config.take_profit) {
          if (config.take_profit.long) {
            setTakeProfitLongUseSignal(config.take_profit.long.use_signal_level || false);
            if (config.take_profit.long.signal_name) setTakeProfitLongSignalName(config.take_profit.long.signal_name);
            if (config.take_profit.long.default_multiplier) setTakeProfitLongMultiplier(config.take_profit.long.default_multiplier);
            if (config.take_profit.long.default_based_on) setTakeProfitLongBasedOn(config.take_profit.long.default_based_on);
          }
          if (config.take_profit.short) {
            setTakeProfitShortUseSignal(config.take_profit.short.use_signal_level || false);
            if (config.take_profit.short.signal_name) setTakeProfitShortSignalName(config.take_profit.short.signal_name);
            if (config.take_profit.short.default_multiplier) setTakeProfitShortMultiplier(config.take_profit.short.default_multiplier);
            if (config.take_profit.short.default_based_on) setTakeProfitShortBasedOn(config.take_profit.short.default_based_on);
          }
        }
        
        // Load position management
        if (config.position_management) {
          if (config.position_management.max_positions) setMaxPositions(config.position_management.max_positions);
          if (config.position_management.allow_multiple_entries !== undefined) {
            setAllowMultipleEntries(config.position_management.allow_multiple_entries);
          }
          if (config.position_management.close_on_opposite_signal !== undefined) {
            setCloseOnOppositeSignal(config.position_management.close_on_opposite_signal);
          }
        }
      }
    }
  }, [open, selectedTemplateId, templates, readyDatasets]);

  // Reset flag when modal closes
  useEffect(() => {
    if (!open) {
      setHasLoadedIntegratedConfig(false);
    }
  }, [open]);

  // Load integrated config (fallback if no template selected) - only once when modal opens
  useEffect(() => {
    if (open && integratedConfig?.backtest && readyDatasets.length > 0 && !selectedTemplateId && !hasLoadedIntegratedConfig) {
      const config = integratedConfig.backtest;
      
      // Load basic config from integrated config
      if (config.initialCapital !== undefined) setInitialCapital(config.initialCapital);
      if (config.positionSize !== undefined) {
        setPositionSizeType('fixed'); // Integrated config uses positionSize (legacy format)
        setPositionSize(config.positionSize);
      }
      
      // Load signal mappings
      if (config.signals) {
        if (config.signals.entry?.long) setEntryLongSignals(config.signals.entry.long);
        if (config.signals.entry?.short) setEntryShortSignals(config.signals.entry.short);
        if (config.signals.exit?.long) setExitLongSignals(config.signals.exit.long);
        if (config.signals.exit?.short) setExitShortSignals(config.signals.exit.short);
      }
      
      // Load SL/TP config
      if (config.stop_loss) {
        if (config.stop_loss.long) {
          setStopLossLongUseSignal(config.stop_loss.long.use_signal_level || false);
          if (config.stop_loss.long.signal_name) setStopLossLongSignalName(config.stop_loss.long.signal_name);
          if (config.stop_loss.long.default_multiplier) setStopLossLongMultiplier(config.stop_loss.long.default_multiplier);
          if (config.stop_loss.long.default_based_on) setStopLossLongBasedOn(config.stop_loss.long.default_based_on);
        }
        if (config.stop_loss.short) {
          setStopLossShortUseSignal(config.stop_loss.short.use_signal_level || false);
          if (config.stop_loss.short.signal_name) setStopLossShortSignalName(config.stop_loss.short.signal_name);
          if (config.stop_loss.short.default_multiplier) setStopLossShortMultiplier(config.stop_loss.short.default_multiplier);
          if (config.stop_loss.short.default_based_on) setStopLossShortBasedOn(config.stop_loss.short.default_based_on);
        }
      }
      
      if (config.take_profit) {
        if (config.take_profit.long) {
          setTakeProfitLongUseSignal(config.take_profit.long.use_signal_level || false);
          if (config.take_profit.long.signal_name) setTakeProfitLongSignalName(config.take_profit.long.signal_name);
          if (config.take_profit.long.default_multiplier) setTakeProfitLongMultiplier(config.take_profit.long.default_multiplier);
          if (config.take_profit.long.default_based_on) setTakeProfitLongBasedOn(config.take_profit.long.default_based_on);
        }
        if (config.take_profit.short) {
          setTakeProfitShortUseSignal(config.take_profit.short.use_signal_level || false);
          if (config.take_profit.short.signal_name) setTakeProfitShortSignalName(config.take_profit.short.signal_name);
          if (config.take_profit.short.default_multiplier) setTakeProfitShortMultiplier(config.take_profit.short.default_multiplier);
          if (config.take_profit.short.default_based_on) setTakeProfitShortBasedOn(config.take_profit.short.default_based_on);
        }
      }
      
      // Load position management
      if (config.position_management) {
        if (config.position_management.max_positions) setMaxPositions(config.position_management.max_positions);
        if (config.position_management.allow_multiple_entries !== undefined) {
          setAllowMultipleEntries(config.position_management.allow_multiple_entries);
        }
        if (config.position_management.close_on_opposite_signal !== undefined) {
          setCloseOnOppositeSignal(config.position_management.close_on_opposite_signal);
        }
      }
      
      // Mark as loaded to prevent re-loading when user edits
      setHasLoadedIntegratedConfig(true);
    }
  }, [open, integratedConfig, readyDatasets, selectedTemplateId, hasLoadedIntegratedConfig]);

  // Load saved templateId from script metadata when modal opens
  useEffect(() => {
    if (open && currentScript?.strategyMetadata) {
      const saved = currentScript.strategyMetadata as any;
      if (saved.templateId && typeof saved.templateId === 'string') {
        // Verify template still exists
        const templateExists = templates.some(t => t.id === saved.templateId);
        if (templateExists) {
          setSelectedTemplateId(saved.templateId);
        }
      }
    }
  }, [open, currentScript?.strategyMetadata, templates]);

  // Load custom configuration from script (fallback if no template and no integrated config)
  useEffect(() => {
    if (open && scriptId && currentScript?.strategyMetadata && readyDatasets.length > 0 && !selectedTemplateId && !integratedConfig?.backtest) {
      setIsLoading(true);
      try {
        const saved = currentScript.strategyMetadata as any;
        
        // Load basic config if available
        if (saved.initialCapital) setInitialCapital(saved.initialCapital);
        if (saved.closeEndOfDay !== undefined) setCloseEndOfDay(saved.closeEndOfDay);
        if (saved.leverage !== undefined) setLeverage(saved.leverage);
        if (saved.marginRequirement !== undefined) setMarginRequirement(saved.marginRequirement);

        // Signal dataset is always synced with script's selectedDatasetId (handled by separate useEffect)
        // Don't load from custom config - always use script's selectedDatasetId
        
        // Load execution dataset config
        // Only activate if executionDatasetId exists AND the dataset is available
        // Check for executionDatasetId first (new format)
        if (saved.executionDatasetId && typeof saved.executionDatasetId === 'string' && saved.executionDatasetId.trim() !== '') {
          const executionDataset = readyDatasets.find(d => d.id === saved.executionDatasetId);
          if (executionDataset) {
            // Dataset exists and is ready - activate checkbox and set dataset
            setUseSeparateExecutionDataset(true);
            setExecutionDatasetId(saved.executionDatasetId);
          } else {
            // Dataset doesn't exist or isn't ready - don't activate checkbox
            setUseSeparateExecutionDataset(false);
            setExecutionDatasetId('');
          }
        } else {
          // No executionDatasetId saved - check legacy support only if no executionDatasetId exists
          // Legacy support: if datasets not set but timeframes are different, try to find matching datasets
          // Only activate if signalTimeframe and executionTimeframe are different (indicating separate execution was intended)
          if (!saved.executionDatasetId && saved.signalTimeframe && saved.executionTimeframe && 
              saved.signalTimeframe !== saved.executionTimeframe && readyDatasets.length > 0) {
            // Try to find datasets matching the timeframes
            const matchingSignal = readyDatasets.find(d => d.timeframe === saved.signalTimeframe);
            const matchingExecution = readyDatasets.find(d => d.timeframe === saved.executionTimeframe);
            if (matchingSignal) setSignalDatasetId(matchingSignal.id);
            if (matchingExecution && matchingExecution.id !== matchingSignal?.id) {
              // Only activate if execution dataset is different from signal dataset
              setUseSeparateExecutionDataset(true);
              setExecutionDatasetId(matchingExecution.id);
            } else {
              setUseSeparateExecutionDataset(false);
              setExecutionDatasetId('');
            }
          } else {
            // No execution dataset configured - checkbox should be off
            setUseSeparateExecutionDataset(false);
            setExecutionDatasetId('');
          }
        }
        // Load capital management (new) or positionSize (legacy)
        if (saved.capitalManagement) {
          setPositionSizeType(saved.capitalManagement.type || 'fixed');
          setPositionSize(saved.capitalManagement.value || 1);
          if (saved.capitalManagement.maxRiskPerTrade) {
            setMaxRiskPerTrade(saved.capitalManagement.maxRiskPerTrade);
          }
        } else if (saved.positionSize) {
          // Legacy support
          setPositionSizeType('fixed');
          setPositionSize(saved.positionSize);
        }
        
        // Load signal mappings
        if (saved.signals) {
          if (saved.signals.entry?.long) setEntryLongSignals(saved.signals.entry.long.filter((s: string) => s));
          if (saved.signals.entry?.short) setEntryShortSignals(saved.signals.entry.short.filter((s: string) => s));
          if (saved.signals.exit?.long) setExitLongSignals(saved.signals.exit.long.filter((s: string) => s));
          if (saved.signals.exit?.short) setExitShortSignals(saved.signals.exit.short.filter((s: string) => s));
        }
        
        // Load SL/TP config (support both new long/short structure and legacy)
        if (saved.stop_loss) {
          // New structure: long/short
          if (saved.stop_loss.long) {
            setStopLossLongUseSignal(saved.stop_loss.long.use_signal_level || false);
            if (saved.stop_loss.long.signal_name) setStopLossLongSignalName(saved.stop_loss.long.signal_name);
            if (saved.stop_loss.long.default_multiplier) setStopLossLongMultiplier(saved.stop_loss.long.default_multiplier);
            if (saved.stop_loss.long.default_based_on) setStopLossLongBasedOn(saved.stop_loss.long.default_based_on);
          }
          if (saved.stop_loss.short) {
            setStopLossShortUseSignal(saved.stop_loss.short.use_signal_level || false);
            if (saved.stop_loss.short.signal_name) setStopLossShortSignalName(saved.stop_loss.short.signal_name);
            if (saved.stop_loss.short.default_multiplier) setStopLossShortMultiplier(saved.stop_loss.short.default_multiplier);
            if (saved.stop_loss.short.default_based_on) setStopLossShortBasedOn(saved.stop_loss.short.default_based_on);
          }
          // Legacy support: apply to both if long/short not specified
          if (!saved.stop_loss.long && !saved.stop_loss.short) {
            const useSignal = saved.stop_loss.use_signal_level || false;
            setStopLossLongUseSignal(useSignal);
            setStopLossShortUseSignal(useSignal);
            if (saved.stop_loss.signal_name) {
              setStopLossLongSignalName(saved.stop_loss.signal_name);
              setStopLossShortSignalName(saved.stop_loss.signal_name);
            }
            if (saved.stop_loss.default_multiplier) {
              setStopLossLongMultiplier(saved.stop_loss.default_multiplier);
              setStopLossShortMultiplier(saved.stop_loss.default_multiplier);
            }
            if (saved.stop_loss.default_based_on) {
              setStopLossLongBasedOn(saved.stop_loss.default_based_on);
              setStopLossShortBasedOn(saved.stop_loss.default_based_on);
            }
          }
        }
        
        if (saved.take_profit) {
          // New structure: long/short
          if (saved.take_profit.long) {
            setTakeProfitLongUseSignal(saved.take_profit.long.use_signal_level || false);
            if (saved.take_profit.long.signal_name) setTakeProfitLongSignalName(saved.take_profit.long.signal_name);
            if (saved.take_profit.long.default_multiplier) setTakeProfitLongMultiplier(saved.take_profit.long.default_multiplier);
            if (saved.take_profit.long.default_based_on) setTakeProfitLongBasedOn(saved.take_profit.long.default_based_on);
          }
          if (saved.take_profit.short) {
            setTakeProfitShortUseSignal(saved.take_profit.short.use_signal_level || false);
            if (saved.take_profit.short.signal_name) setTakeProfitShortSignalName(saved.take_profit.short.signal_name);
            if (saved.take_profit.short.default_multiplier) setTakeProfitShortMultiplier(saved.take_profit.short.default_multiplier);
            if (saved.take_profit.short.default_based_on) setTakeProfitShortBasedOn(saved.take_profit.short.default_based_on);
          }
          // Legacy support: apply to both if long/short not specified
          if (!saved.take_profit.long && !saved.take_profit.short) {
            const useSignal = saved.take_profit.use_signal_level || false;
            setTakeProfitLongUseSignal(useSignal);
            setTakeProfitShortUseSignal(useSignal);
            if (saved.take_profit.signal_name) {
              setTakeProfitLongSignalName(saved.take_profit.signal_name);
              setTakeProfitShortSignalName(saved.take_profit.signal_name);
            }
            if (saved.take_profit.default_multiplier) {
              setTakeProfitLongMultiplier(saved.take_profit.default_multiplier);
              setTakeProfitShortMultiplier(saved.take_profit.default_multiplier);
            }
            if (saved.take_profit.default_based_on) {
              setTakeProfitLongBasedOn(saved.take_profit.default_based_on);
              setTakeProfitShortBasedOn(saved.take_profit.default_based_on);
            }
          }
        }
        
        // Load position management
        if (saved.position_management) {
          if (saved.position_management.max_positions) setMaxPositions(saved.position_management.max_positions);
          if (saved.position_management.allow_multiple_entries !== undefined) {
            setAllowMultipleEntries(saved.position_management.allow_multiple_entries);
          }
          if (saved.position_management.close_on_opposite_signal !== undefined) {
            setCloseOnOppositeSignal(saved.position_management.close_on_opposite_signal);
          }
        }
      } catch (error) {
        console.error('Failed to load custom configuration:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [open, scriptId, currentScript]);

  // Validation
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Basic validation
    if (initialCapital <= 0) {
      errors.initialCapital = 'Initial capital must be greater than 0';
    }
    // Validate position size based on type
    if (positionSize <= 0) {
      errors.positionSize = 'Position size value must be greater than 0';
    }
    if (positionSizeType === 'percentage' && positionSize > 100) {
      errors.positionSize = 'Percentage cannot exceed 100%';
    }
    if (positionSizeType === 'risk_per_trade') {
      if (maxRiskPerTrade <= 0 || maxRiskPerTrade > 100) {
        errors.maxRiskPerTrade = 'Max risk per trade must be between 0 and 100%';
      }
    }

    // Leverage validation
    if (leverage !== undefined && leverage < 1) {
      errors.leverage = 'Leverage must be 1 or higher';
    }

    // Margin requirement validation
    if (marginRequirement !== undefined && (marginRequirement <= 0 || marginRequirement > 100)) {
      errors.marginRequirement = 'Margin requirement must be between 0 and 100%';
    }
    // Validate datasets
    if (!signalDatasetId) {
      errors.signalDataset = 'Please select a dataset for signal generation';
    }
    // Validate execution dataset only if separate execution dataset is enabled
    if (useSeparateExecutionDataset && !executionDatasetId) {
      errors.executionDataset = 'Please select a dataset for trade execution';
    }
    // Validate timeframe relationship if both datasets are selected and separate execution is enabled
    if (useSeparateExecutionDataset && signalDataset && executionDataset && signalDataset.timeframe && executionDataset.timeframe) {
      const signalSeconds = timeframeToSeconds(signalDataset.timeframe);
      const executionSeconds = timeframeToSeconds(executionDataset.timeframe);
      if (executionSeconds > signalSeconds) {
        errors.executionDataset = 'Execution dataset timeframe must be equal or lower than signal dataset timeframe';
      }
    }
    
    // Signal validation
    if (entryLongSignals.length === 0 && entryShortSignals.length === 0) {
      errors.entrySignals = 'At least one entry signal (long or short) is required';
    }
    
    // Validate signal names exist
    const allSelectedSignals = [
      ...entryLongSignals,
      ...entryShortSignals,
      ...exitLongSignals,
      ...exitShortSignals,
      ...(stopLossLongUseSignal ? [stopLossLongSignalName] : []),
      ...(stopLossShortUseSignal ? [stopLossShortSignalName] : []),
      ...(takeProfitLongUseSignal ? [takeProfitLongSignalName] : []),
      ...(takeProfitShortUseSignal ? [takeProfitShortSignalName] : []),
    ].filter(Boolean);
    
    const invalidSignals = allSelectedSignals.filter(s => !availableSignals.includes(s));
    if (invalidSignals.length > 0) {
      errors.invalidSignals = `Invalid signals: ${invalidSignals.join(', ')}`;
    }
    
    // SL/TP validation - Long
    if (stopLossLongUseSignal && !stopLossLongSignalName) {
      errors.stopLossLongSignal = 'Please select a signal for stop loss (Long)';
    }
    if (takeProfitLongUseSignal && !takeProfitLongSignalName) {
      errors.takeProfitLongSignal = 'Please select a signal for take profit (Long)';
    }
    if (!stopLossLongUseSignal && stopLossLongMultiplier <= 0) {
      errors.stopLossLongMultiplier = 'Stop loss multiplier (Long) must be greater than 0';
    }
    if (!takeProfitLongUseSignal && takeProfitLongMultiplier <= 0) {
      errors.takeProfitLongMultiplier = 'Take profit multiplier (Long) must be greater than 0';
    }
    
    // SL/TP validation - Short
    if (stopLossShortUseSignal && !stopLossShortSignalName) {
      errors.stopLossShortSignal = 'Please select a signal for stop loss (Short)';
    }
    if (takeProfitShortUseSignal && !takeProfitShortSignalName) {
      errors.takeProfitShortSignal = 'Please select a signal for take profit (Short)';
    }
    if (!stopLossShortUseSignal && stopLossShortMultiplier <= 0) {
      errors.stopLossShortMultiplier = 'Stop loss multiplier (Short) must be greater than 0';
    }
    if (!takeProfitShortUseSignal && takeProfitShortMultiplier <= 0) {
      errors.takeProfitShortMultiplier = 'Take profit multiplier (Short) must be greater than 0';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showErrorToast('Please fix validation errors before saving');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Build strategy metadata
      const strategyMetadata: StrategyMetadata = {
        signals: {
          entry: {
            long: entryLongSignals,
            short: entryShortSignals,
          },
          exit: {
            long: exitLongSignals,
            short: exitShortSignals,
          },
        },
        stop_loss: {
          long: {
            use_signal_level: stopLossLongUseSignal,
            signal_name: stopLossLongUseSignal ? stopLossLongSignalName : undefined,
            default_multiplier: stopLossLongUseSignal ? undefined : stopLossLongMultiplier,
            default_based_on: stopLossLongUseSignal ? undefined : stopLossLongBasedOn,
          },
          short: {
            use_signal_level: stopLossShortUseSignal,
            signal_name: stopLossShortUseSignal ? stopLossShortSignalName : undefined,
            default_multiplier: stopLossShortUseSignal ? undefined : stopLossShortMultiplier,
            default_based_on: stopLossShortUseSignal ? undefined : stopLossShortBasedOn,
          },
        },
        take_profit: {
          long: {
            use_signal_level: takeProfitLongUseSignal,
            signal_name: takeProfitLongUseSignal ? takeProfitLongSignalName : undefined,
            default_multiplier: takeProfitLongUseSignal ? undefined : takeProfitLongMultiplier,
            default_based_on: takeProfitLongUseSignal ? undefined : takeProfitLongBasedOn,
          },
          short: {
            use_signal_level: takeProfitShortUseSignal,
            signal_name: takeProfitShortUseSignal ? takeProfitShortSignalName : undefined,
            default_multiplier: takeProfitShortUseSignal ? undefined : takeProfitShortMultiplier,
            default_based_on: takeProfitShortUseSignal ? undefined : takeProfitShortBasedOn,
          },
        },
        position_management: {
          max_positions: maxPositions,
          allow_multiple_entries: allowMultipleEntries,
          close_on_opposite_signal: closeOnOppositeSignal,
        },
      };

      // Determine final execution dataset (use signal dataset if separate execution is disabled)
      const finalExecutionDatasetId = useSeparateExecutionDataset ? executionDatasetId : signalDatasetId;
      const finalExecutionDataset = useSeparateExecutionDataset ? executionDataset : signalDataset;
      
      // Build complete config
      const config: BacktestConfigForm = {
        initialCapital,
        signalDatasetId,
        executionDatasetId: useSeparateExecutionDataset ? finalExecutionDatasetId : undefined,
        // Include timeframes for legacy support (derived from datasets)
        signalTimeframe: signalDataset?.timeframe || '',
        executionTimeframe: finalExecutionDataset?.timeframe || '',
        positionSize, // Legacy support
        capitalManagement: {
          type: positionSizeType,
          value: positionSize,
          ...(positionSizeType === 'risk_per_trade' && { maxRiskPerTrade }),
        },
        closeEndOfDay,
        leverage,
        marginRequirement,
        strategyMetadata,
      };

      // Save to script (include full config in strategyMetadata for now)
      // Note: signalDatasetId is NOT saved here - it always comes from script's selectedDatasetId
      // We save executionDatasetId only if useSeparateExecutionDataset is true, otherwise remove it
      const savedMetadata = { ...strategyMetadata };
      
      // Build the metadata to save
      const metadataToSave: any = {
        ...savedMetadata,
        initialCapital,
        closeEndOfDay,
        ...(leverage !== undefined && { leverage }),
        ...(marginRequirement !== undefined && { marginRequirement }),
        // signalDatasetId is NOT saved - always use script's selectedDatasetId
        // Include timeframes for legacy support (derived from datasets)
        signalTimeframe: signalDataset?.timeframe || '',
        executionTimeframe: finalExecutionDataset?.timeframe || '',
        positionSize,
      };
      
      // Handle executionDatasetId: save if enabled, remove if disabled
      if (useSeparateExecutionDataset && finalExecutionDatasetId) {
        metadataToSave.executionDatasetId = finalExecutionDatasetId;
      } else {
        // Explicitly remove executionDatasetId if checkbox is disabled
        delete metadataToSave.executionDatasetId;
      }
      
      // Save templateId if a template is selected, otherwise remove it
      if (selectedTemplateId) {
        metadataToSave.templateId = selectedTemplateId;
      } else {
        delete metadataToSave.templateId;
      }
      
      const result = await scriptsApi.update(scriptId, {
        strategyMetadata: metadataToSave,
      });
      
      // Update the script in the store to reflect the custom configuration
      if (result.script) {
        useScriptStore.getState().setCurrentScript(result.script);
      }

      showSuccessToast('Backtest configuration saved successfully');
      onConfigSaved?.(config);
      onOpenChange(false);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to save backtest configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setInitialCapital(10000);
    // Reset to script's selectedDatasetId if available
    if (currentScript?.selectedDatasetId) {
      setSignalDatasetId(currentScript.selectedDatasetId);
    } else {
      setSignalDatasetId('');
    }
    setUseSeparateExecutionDataset(false);
    setExecutionDatasetId('');
    setPositionSizeType('fixed');
    setPositionSize(1);
    setMaxRiskPerTrade(2.0);
    setEntryLongSignals([]);
    setEntryShortSignals([]);
    setExitLongSignals([]);
    setExitShortSignals([]);
    // Reset SL/TP - Long
    setStopLossLongUseSignal(false);
    setStopLossLongSignalName('');
    setStopLossLongMultiplier(2.0);
    setStopLossLongBasedOn('atr');
    setTakeProfitLongUseSignal(false);
    setTakeProfitLongSignalName('');
    setTakeProfitLongMultiplier(3.0);
    setTakeProfitLongBasedOn('atr');
    
    // Reset SL/TP - Short
    setStopLossShortUseSignal(false);
    setStopLossShortSignalName('');
    setStopLossShortMultiplier(2.0);
    setStopLossShortBasedOn('atr');
    setTakeProfitShortUseSignal(false);
    setTakeProfitShortSignalName('');
    setTakeProfitShortMultiplier(3.0);
    setTakeProfitShortBasedOn('atr');
    setMaxPositions(1);
    setAllowMultipleEntries(false);
    setCloseOnOppositeSignal(true);
  };

  // Get available signals for selection (exclude already selected ones)
  const getAvailableSignalsForSelection = (excludeSignals: string[] = []) => {
    return availableSignals.filter(s => !excludeSignals.includes(s));
  };

  // Convert current config to YAML format using professional YAML library
  const convertConfigToYAML = (): string => {
    // Structure must match the DSL format: config: -> backtest: -> ...
    const config: any = {
      config: {
        backtest: {
          signals: {
            entry: {
              long: entryLongSignals.filter(Boolean),
              short: entryShortSignals.filter(Boolean),
            },
            exit: {
              long: exitLongSignals.filter(Boolean),
              short: exitShortSignals.filter(Boolean),
            },
          },
          stop_loss: {
            long: {
              default_based_on: stopLossLongBasedOn,
              use_signal_level: stopLossLongUseSignal,
              ...(stopLossLongUseSignal ? { signal_name: stopLossLongSignalName } : { default_multiplier: stopLossLongMultiplier }),
            },
            short: {
              default_based_on: stopLossShortBasedOn,
              use_signal_level: stopLossShortUseSignal,
              ...(stopLossShortUseSignal ? { signal_name: stopLossShortSignalName } : { default_multiplier: stopLossShortMultiplier }),
            },
          },
          take_profit: {
            long: {
              default_based_on: takeProfitLongBasedOn,
              use_signal_level: takeProfitLongUseSignal,
              ...(takeProfitLongUseSignal ? { signal_name: takeProfitLongSignalName } : { default_multiplier: takeProfitLongMultiplier }),
            },
            short: {
              default_based_on: takeProfitShortBasedOn,
              use_signal_level: takeProfitShortUseSignal,
              ...(takeProfitShortUseSignal ? { signal_name: takeProfitShortSignalName } : { default_multiplier: takeProfitShortMultiplier }),
            },
          },
          initialCapital,
          positionSize,
          position_management: {
            max_positions: maxPositions,
            allow_multiple_entries: allowMultipleEntries,
            close_on_opposite_signal: closeOnOppositeSignal,
          },
        },
      },
    };

    // Use professional YAML library for robust stringification
    // This ensures compatibility with the backend parser and handles edge cases correctly
    const yamlString = stringifyYAML(config, {
      indent: 2,
      lineWidth: 0, // No line wrapping
      simpleKeys: false,
      defaultStringType: 'PLAIN', // Use plain strings when possible
      defaultKeyType: 'PLAIN',
    });
    
    return yamlString;
  };

  // Build template config object (reusable)
  const buildTemplateConfig = () => {
    return {
      signals: {
        entry: {
          long: entryLongSignals.filter(Boolean),
          short: entryShortSignals.filter(Boolean),
        },
        exit: {
          long: exitLongSignals.filter(Boolean),
          short: exitShortSignals.filter(Boolean),
        },
      },
      stop_loss: {
        long: {
          default_based_on: stopLossLongBasedOn,
          use_signal_level: stopLossLongUseSignal,
          ...(stopLossLongUseSignal ? { signal_name: stopLossLongSignalName } : { default_multiplier: stopLossLongMultiplier }),
        },
        short: {
          default_based_on: stopLossShortBasedOn,
          use_signal_level: stopLossShortUseSignal,
          ...(stopLossShortUseSignal ? { signal_name: stopLossShortSignalName } : { default_multiplier: stopLossShortMultiplier }),
        },
      },
      take_profit: {
        long: {
          default_based_on: takeProfitLongBasedOn,
          use_signal_level: takeProfitLongUseSignal,
          ...(takeProfitLongUseSignal ? { signal_name: takeProfitLongSignalName } : { default_multiplier: takeProfitLongMultiplier }),
        },
        short: {
          default_based_on: takeProfitShortBasedOn,
          use_signal_level: takeProfitShortUseSignal,
          ...(takeProfitShortUseSignal ? { signal_name: takeProfitShortSignalName } : { default_multiplier: takeProfitShortMultiplier }),
        },
      },
      initialCapital,
      positionSize,
      position_management: {
        max_positions: maxPositions,
        allow_multiple_entries: allowMultipleEntries,
        close_on_opposite_signal: closeOnOppositeSignal,
      },
    };
  };

  // Handle load from config block (default/integrated config)
  const handleLoadFromConfigBlock = () => {
    if (!integratedConfig?.backtest) {
      showErrorToast('No config block found in DSL script');
      return;
    }

    // Clear template selection to use integrated config
    setSelectedTemplateId('');

    // Load config from integrated config
    const config = integratedConfig.backtest;
    
    // Reset the flag so we can reload
    setHasLoadedIntegratedConfig(false);
    
    // Load basic config
    if (config.initialCapital !== undefined) setInitialCapital(config.initialCapital);
    if (config.positionSize !== undefined) {
      setPositionSizeType('fixed');
      setPositionSize(config.positionSize);
    }
    
    // Load signal mappings
    if (config.signals) {
      if (config.signals.entry?.long) setEntryLongSignals(config.signals.entry.long);
      if (config.signals.entry?.short) setEntryShortSignals(config.signals.entry.short);
      if (config.signals.exit?.long) setExitLongSignals(config.signals.exit.long);
      if (config.signals.exit?.short) setExitShortSignals(config.signals.exit.short);
    }
    
    // Load SL/TP config
    if (config.stop_loss) {
      if (config.stop_loss.long) {
        setStopLossLongUseSignal(config.stop_loss.long.use_signal_level || false);
        if (config.stop_loss.long.signal_name) setStopLossLongSignalName(config.stop_loss.long.signal_name);
        if (config.stop_loss.long.default_multiplier) setStopLossLongMultiplier(config.stop_loss.long.default_multiplier);
        if (config.stop_loss.long.default_based_on) setStopLossLongBasedOn(config.stop_loss.long.default_based_on);
      }
      if (config.stop_loss.short) {
        setStopLossShortUseSignal(config.stop_loss.short.use_signal_level || false);
        if (config.stop_loss.short.signal_name) setStopLossShortSignalName(config.stop_loss.short.signal_name);
        if (config.stop_loss.short.default_multiplier) setStopLossShortMultiplier(config.stop_loss.short.default_multiplier);
        if (config.stop_loss.short.default_based_on) setStopLossShortBasedOn(config.stop_loss.short.default_based_on);
      }
    }
    
    if (config.take_profit) {
      if (config.take_profit.long) {
        setTakeProfitLongUseSignal(config.take_profit.long.use_signal_level || false);
        if (config.take_profit.long.signal_name) setTakeProfitLongSignalName(config.take_profit.long.signal_name);
        if (config.take_profit.long.default_multiplier) setTakeProfitLongMultiplier(config.take_profit.long.default_multiplier);
        if (config.take_profit.long.default_based_on) setTakeProfitLongBasedOn(config.take_profit.long.default_based_on);
      }
      if (config.take_profit.short) {
        setTakeProfitShortUseSignal(config.take_profit.short.use_signal_level || false);
        if (config.take_profit.short.signal_name) setTakeProfitShortSignalName(config.take_profit.short.signal_name);
        if (config.take_profit.short.default_multiplier) setTakeProfitShortMultiplier(config.take_profit.short.default_multiplier);
        if (config.take_profit.short.default_based_on) setTakeProfitShortBasedOn(config.take_profit.short.default_based_on);
      }
    }
    
    // Load position management
    if (config.position_management) {
      if (config.position_management.max_positions) setMaxPositions(config.position_management.max_positions);
      if (config.position_management.allow_multiple_entries !== undefined) {
        setAllowMultipleEntries(config.position_management.allow_multiple_entries);
      }
      if (config.position_management.close_on_opposite_signal !== undefined) {
        setCloseOnOppositeSignal(config.position_management.close_on_opposite_signal);
      }
    }

    showSuccessToast('Configuration loaded from DSL config block');
  };

  // Handle save as template - opens dialog
  const handleSaveAsTemplate = () => {
    if (!validate()) {
      showErrorToast('Please fix validation errors before saving as template');
      return;
    }

    // Reset dialog state
    setTemplateAction('create');
    setNewTemplateName('');
    setSelectedTemplateToUpdate('');
    setSaveTemplateDialogOpen(true);
  };

  // Handle update current template (quick update without dialog)
  const handleUpdateCurrentTemplate = async () => {
    if (!selectedTemplateId) {
      showErrorToast('No template selected');
      return;
    }

    if (!validate()) {
      showErrorToast('Please fix validation errors before updating template');
      return;
    }

    // Verify template exists before attempting update
    const currentTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!currentTemplate) {
      showErrorToast('Template not found. It may have been deleted. Please reload templates.');
      // Clear selection and refresh templates
      setSelectedTemplateId('');
      try {
        const updatedTemplates = await settingsTemplatesApi.list({ 
          type: SettingsTemplateType.BACKTEST_CONFIG,
          scriptId: scriptId 
        });
        setTemplates(Array.isArray(updatedTemplates) ? updatedTemplates : []);
      } catch (error) {
        console.error('Failed to refresh templates:', error);
      }
      return;
    }

    setIsSaving(true);
    try {
      const templateConfig = buildTemplateConfig();

      await settingsTemplatesApi.update(selectedTemplateId, {
        config: templateConfig,
      });

      // Refresh templates list
      const updatedTemplates = await settingsTemplatesApi.list({ 
        type: SettingsTemplateType.BACKTEST_CONFIG,
        scriptId: scriptId 
      });
      setTemplates(Array.isArray(updatedTemplates) ? updatedTemplates : []);
      
      showSuccessToast(`${currentTemplate.name} updated successfully`);
    } catch (error: any) {
      // Handle 404 specifically
      if (error?.response?.status === 404) {
        showErrorToast('Template not found. It may have been deleted. Please select a different template.');
        // Clear selection and refresh templates
        setSelectedTemplateId('');
        try {
          const updatedTemplates = await settingsTemplatesApi.list({ 
            type: SettingsTemplateType.BACKTEST_CONFIG,
            scriptId: scriptId 
          });
          setTemplates(Array.isArray(updatedTemplates) ? updatedTemplates : []);
        } catch (refreshError) {
          console.error('Failed to refresh templates:', refreshError);
        }
      } else {
        showErrorToast(error.message || 'Failed to update template');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle template save (create or update)
  const handleTemplateSave = async () => {
    try {
      const templateConfig = buildTemplateConfig();

      if (templateAction === 'create') {
        if (!newTemplateName.trim()) {
          showErrorToast('Please enter a template name');
          return;
        }

        await settingsTemplatesApi.create({
          name: newTemplateName.trim(),
          type: SettingsTemplateType.BACKTEST_CONFIG,
          config: templateConfig,
          scriptId: scriptId,
        });

        showSuccessToast('Template created successfully');
      } else {
        if (!selectedTemplateToUpdate) {
          showErrorToast('Please select a template to update');
          return;
        }

        await settingsTemplatesApi.update(selectedTemplateToUpdate, {
          config: templateConfig,
        });

        showSuccessToast('Template updated successfully');
      }

      // Refresh templates list
      const updatedTemplates = await settingsTemplatesApi.list({ 
        type: SettingsTemplateType.BACKTEST_CONFIG,
        scriptId: scriptId 
      });
      setTemplates(Array.isArray(updatedTemplates) ? updatedTemplates : []);
      
      // Close dialog
      setSaveTemplateDialogOpen(false);
      setNewTemplateName('');
      setSelectedTemplateToUpdate('');
    } catch (error: any) {
      showErrorToast(error.message || `Failed to ${templateAction === 'create' ? 'create' : 'update'} template`);
    }
  };

  // Handle save as YAML in DSL
  const handleSaveAsYAML = async () => {
    if (!validate()) {
      showErrorToast('Please fix validation errors before updating config block');
      return;
    }

    setIsSaving(true);
    
    try {
      // First, save the configuration to the database (same logic as handleSave)
      const strategyMetadata: StrategyMetadata = {
        signals: {
          entry: {
            long: entryLongSignals,
            short: entryShortSignals,
          },
          exit: {
            long: exitLongSignals,
            short: exitShortSignals,
          },
        },
        stop_loss: {
          long: {
            use_signal_level: stopLossLongUseSignal,
            signal_name: stopLossLongUseSignal ? stopLossLongSignalName : undefined,
            default_multiplier: stopLossLongUseSignal ? undefined : stopLossLongMultiplier,
            default_based_on: stopLossLongUseSignal ? undefined : stopLossLongBasedOn,
          },
          short: {
            use_signal_level: stopLossShortUseSignal,
            signal_name: stopLossShortUseSignal ? stopLossShortSignalName : undefined,
            default_multiplier: stopLossShortUseSignal ? undefined : stopLossShortMultiplier,
            default_based_on: stopLossShortUseSignal ? undefined : stopLossShortBasedOn,
          },
        },
        take_profit: {
          long: {
            use_signal_level: takeProfitLongUseSignal,
            signal_name: takeProfitLongUseSignal ? takeProfitLongSignalName : undefined,
            default_multiplier: takeProfitLongUseSignal ? undefined : takeProfitLongMultiplier,
            default_based_on: takeProfitLongUseSignal ? undefined : takeProfitLongBasedOn,
          },
          short: {
            use_signal_level: takeProfitShortUseSignal,
            signal_name: takeProfitShortUseSignal ? takeProfitShortSignalName : undefined,
            default_multiplier: takeProfitShortUseSignal ? undefined : takeProfitShortMultiplier,
            default_based_on: takeProfitShortUseSignal ? undefined : takeProfitShortBasedOn,
          },
        },
        position_management: {
          max_positions: maxPositions,
          allow_multiple_entries: allowMultipleEntries,
          close_on_opposite_signal: closeOnOppositeSignal,
        },
      };

      // Determine final execution dataset (use signal dataset if separate execution is disabled)
      const finalExecutionDatasetId = useSeparateExecutionDataset ? executionDatasetId : signalDatasetId;
      const finalExecutionDataset = useSeparateExecutionDataset ? executionDataset : signalDataset;
      
      // Build the metadata to save
      const metadataToSave: any = {
        ...strategyMetadata,
        initialCapital,
        closeEndOfDay,
        ...(leverage !== undefined && { leverage }),
        ...(marginRequirement !== undefined && { marginRequirement }),
        signalTimeframe: signalDataset?.timeframe || '',
        executionTimeframe: finalExecutionDataset?.timeframe || '',
        positionSize,
      };
      
      // Handle executionDatasetId: save if enabled, remove if disabled
      if (useSeparateExecutionDataset && finalExecutionDatasetId) {
        metadataToSave.executionDatasetId = finalExecutionDatasetId;
      } else {
        delete metadataToSave.executionDatasetId;
      }
      
      // Save templateId if a template is selected, otherwise remove it
      if (selectedTemplateId) {
        metadataToSave.templateId = selectedTemplateId;
      } else {
        delete metadataToSave.templateId;
      }
      
      // Save configuration to database first
      const saveResult = await scriptsApi.update(scriptId, {
        strategyMetadata: metadataToSave,
      });
      
      // Update the script in the store
      if (saveResult.script) {
        useScriptStore.getState().setCurrentScript(saveResult.script);
      }

      // Now update the config block in the DSL script
      const yamlConfig = convertConfigToYAML();
      
      // Ask user if they want to update config block in DSL
      const confirmInsert = confirm(
        'Configuration saved to database.\n\n' +
        'This will also update the config block in your DSL script.\n\n' +
        'If a config block exists, it will be updated. Otherwise, a new config block will be created at the beginning.\n\n' +
        'Do you want to continue?'
      );
      
      if (!confirmInsert) {
        setIsSaving(false);
        showSuccessToast('Configuration saved to database');
        return;
      }

      // Get current script (use latest from store to ensure we have the most recent dslCode)
      const currentScript = useScriptStore.getState().currentScript;
      if (!currentScript) {
        showErrorToast('No script found');
        setIsSaving(false);
        return;
      }

      // Use the latest dslCode from the script store (may be more recent than prop)
      const latestDslCode = currentScript.dslCode || dslCode;

      // Find config block using the same logic as the extractor
      const configMatch = latestDslCode.match(/^config:\s*$/m);
      let newDslCode: string;
      
      if (configMatch) {
        // Config block exists - replace its content
        const configStartIndex = configMatch.index!;
        const lines = latestDslCode.split('\n');
        const configStartLine = latestDslCode.substring(0, configStartIndex).split('\n').length - 1;
        
        // Find where the config block ends (same logic as extractor)
        let baseIndent = 0;
        let configEndLine = configStartLine;
        
        for (let i = configStartLine + 1; i < lines.length; i++) {
          const line = lines[i];
          if (line === undefined) continue;
          
          // Skip empty lines
          if (line.trim() === '') {
            continue;
          }
          
          // Calculate indentation
          const indentMatch = line.match(/^(\s*)/);
          const indent = indentMatch?.[1]?.length || 0;
          
          // Set base indent from first non-empty line after config:
          if (baseIndent === 0 && line.trim() !== '') {
            if (indent === 0) {
              // Config block ended (empty config block)
              configEndLine = i;
              break;
            }
            baseIndent = indent;
            configEndLine = i;
            continue;
          }
          
          // If indentation is less than or equal to base level, config block ended
          if (indent <= baseIndent && line.trim() !== '') {
            configEndLine = i;
            break;
          }
          
          configEndLine = i;
        }
        
        // Extract YAML content (everything after "config:")
        // The yamlConfig includes "config:" at the top level, so we need to extract the content under it
        // yamlConfig format: "config:\n  backtest:\n    ..."
        // We need to extract everything after "config:" and keep the correct indentation (2 spaces)
        const yamlLines = yamlConfig.split('\n');
        let yamlContent: string;
        
        if (yamlLines[0] === 'config:') {
          // Remove "config:" line and keep the rest with its original indentation
          // The content after "config:" already has the correct indentation (2 spaces for backtest:, etc.)
          // We just need to remove the first line and keep everything else as-is
          yamlContent = yamlLines.slice(1).join('\n');
        } else {
          // Fallback: try to extract content after "config:"
          yamlContent = yamlConfig.replace(/^config:\s*\n/, ''); // Remove "config:" line
        }
        
        // Replace the config block content while keeping the "config:" line
        const beforeConfig = lines.slice(0, configStartLine + 1).join('\n'); // Include "config:" line
        const afterConfig = lines.slice(configEndLine + 1).join('\n');
        
        // Reconstruct with new YAML content
        newDslCode = [
          beforeConfig,
          yamlContent, // YAML content without "config:" prefix but with correct 2-space indentation
          afterConfig
        ].filter(Boolean).join('\n');
      } else {
        // No config block exists - create it at the beginning
        // The yamlConfig already includes "config:" at the top level, so we can use it directly
        newDslCode = yamlConfig + '\n\n' + latestDslCode.trim();
      }
      
      // Debug: log the generated code to help troubleshoot
      console.log('Generated DSL code (first 500 chars):', newDslCode.substring(0, 500));

      // Update script
      const result = await scriptsApi.update(currentScript.id, {
        dslCode: newDslCode,
      });

      // Update script in store
      if (result.script) {
        useScriptStore.getState().setCurrentScript(result.script);
      }
      
      showSuccessToast('Configuration saved and config block updated in DSL script');
      onOpenChange(false);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to save configuration or update config block');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {t('scripts.configureBacktest') || 'Configure Backtest'}
            </DialogTitle>
            <DialogDescription>
              {t('scripts.configureBacktestDescription') || 'Configure backtesting parameters for your strategy. The engine will interpret signals and execute trades vela a vela.'}
            </DialogDescription>
          </DialogHeader>


          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <p className="text-sm text-muted-foreground">{t('scripts.loadingCustomConfiguration') || 'Loading custom configuration...'}</p>
            </div>
          )}

          {/* Integrated Config Banner */}
          {integratedConfig?.backtest && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {t('scripts.usingIntegratedConfiguration') || 'Using integrated configuration from DSL'}
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    {t('scripts.usingIntegratedConfigurationDescription') || 'Configuration is loaded from the config: block in your script. You can override these values below.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Config Warnings */}
          {configWarnings.length > 0 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                    {t('scripts.configurationWarnings') || 'Configuration Warnings'}
                  </p>
                  <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 list-disc list-inside space-y-1">
                    {configWarnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Config Errors */}
          {configErrors.length > 0 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900 dark:text-red-100">
                    {t('scripts.configurationErrors') || 'Configuration Errors'}
                  </p>
                  <ul className="text-xs text-red-700 dark:text-red-300 mt-1 list-disc list-inside space-y-1">
                    {configErrors.map((error, idx) => (
                      <li key={idx}>
                        <strong>{error.field}:</strong> {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Template Loaded Banner */}
          {selectedTemplateId && (
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {t('scripts.templateConfigurationLoaded') || 'Template configuration loaded. You can override values below.'}
              </p>
            </div>
          )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Basic
            </TabsTrigger>
            <TabsTrigger value="signals" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Signals
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Advanced
            </TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor="initialCapital">Initial Capital</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Starting capital for backtesting. This is the initial amount of money available for trading.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    id="initialCapital"
                    type="number"
                    min="1"
                    step="100"
                    value={initialCapital}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setInitialCapital(value);
                      if (value <= 0) {
                        setValidationErrors(prev => ({ ...prev, initialCapital: 'Must be greater than 0' }));
                      } else {
                        setValidationErrors(prev => {
                          const { initialCapital, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    className={validationErrors.initialCapital ? 'border-red-500' : ''}
                  />
                  {validationErrors.initialCapital && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {validationErrors.initialCapital}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Starting capital for backtesting
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor="positionSizeType">Position Size Type</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">
                          <strong>Fixed:</strong> Fixed number of contracts/shares per trade.<br/>
                          <strong>Percentage:</strong> Percentage of available capital per trade.<br/>
                          <strong>Risk per Trade:</strong> Position size based on risk percentage (uses stop loss distance).
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select value={positionSizeType} onValueChange={(v: 'fixed' | 'percentage' | 'risk_per_trade') => setPositionSizeType(v)}>
                    <SelectTrigger id="positionSizeType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="risk_per_trade">Risk per Trade</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="positionSize">
                    {positionSizeType === 'fixed' ? 'Contracts/Shares' : 
                     positionSizeType === 'percentage' ? 'Percentage (%)' : 
                     'Risk Percentage (%)'}
                  </Label>
                  <Input
                    id="positionSize"
                    type="number"
                    min="0.01"
                    step={positionSizeType === 'percentage' || positionSizeType === 'risk_per_trade' ? "0.1" : "0.01"}
                    max={positionSizeType === 'percentage' ? 100 : undefined}
                    value={positionSize}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setPositionSize(value);
                      if (value <= 0) {
                        setValidationErrors(prev => ({ ...prev, positionSize: 'Must be greater than 0' }));
                      } else if (positionSizeType === 'percentage' && value > 100) {
                        setValidationErrors(prev => ({ ...prev, positionSize: 'Percentage cannot exceed 100%' }));
                      } else {
                        setValidationErrors(prev => {
                          const { positionSize, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    className={validationErrors.positionSize ? 'border-red-500' : ''}
                  />
                  {validationErrors.positionSize && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {validationErrors.positionSize}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {positionSizeType === 'fixed' ? 'Number of contracts/shares per trade' :
                     positionSizeType === 'percentage' ? 'Percentage of available capital to use per trade' :
                     'Maximum risk percentage per trade (based on stop loss distance)'}
                  </p>
                </div>

                {positionSizeType === 'risk_per_trade' && (
                  <div className="space-y-2">
                    <Label htmlFor="maxRiskPerTrade">Max Risk Per Trade (%)</Label>
                    <Input
                      id="maxRiskPerTrade"
                      type="number"
                      min="0.1"
                      max="100"
                      step="0.1"
                      value={maxRiskPerTrade}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setMaxRiskPerTrade(value);
                        if (value <= 0 || value > 100) {
                          setValidationErrors(prev => ({ ...prev, maxRiskPerTrade: 'Must be between 0.1 and 100%' }));
                        } else {
                          setValidationErrors(prev => {
                            const { maxRiskPerTrade, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      className={validationErrors.maxRiskPerTrade ? 'border-red-500' : ''}
                    />
                    {validationErrors.maxRiskPerTrade && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.maxRiskPerTrade}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Maximum risk percentage per trade when calculating position size based on stop loss
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm font-semibold">Dataset Selection</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Select datasets for signal generation and trade execution. Timeframes are automatically determined from the selected datasets.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="signalDataset">Signal Dataset</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            Automatically uses the dataset selected in the script. The timeframe of this dataset determines the signal generation frequency. 
                            Higher timeframes (e.g., 5min, 15min) produce cleaner, less noisy signals.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select 
                      value={signalDatasetId} 
                      disabled={true}
                    >
                      <SelectTrigger id="signalDataset" className={validationErrors.signalDataset ? 'border-red-500' : 'bg-muted cursor-not-allowed'}>
                        <SelectValue placeholder={currentScript?.selectedDatasetId ? "Auto (from script)" : "No dataset selected in script"} />
                      </SelectTrigger>
                      <SelectContent>
                        {readyDatasets.map((dataset) => (
                          <SelectItem key={dataset.id} value={dataset.id}>
                            {dataset.name} {dataset.timeframe && `(${dataset.timeframe})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {currentScript?.selectedDatasetId && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Automatically synced with script's selected dataset
                      </p>
                    )}
                    {validationErrors.signalDataset && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.signalDataset}
                      </p>
                    )}
                    {signalDataset && signalDataset.timeframe && (
                      <div className="p-2 bg-muted rounded-md">
                        <p className="text-xs text-muted-foreground">
                          <strong>Timeframe:</strong> {signalDataset.timeframe}
                        </p>
                        {signalDataset.symbol && (
                          <p className="text-xs text-muted-foreground">
                            <strong>Symbol:</strong> {signalDataset.symbol}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="useSeparateExecution"
                        checked={useSeparateExecutionDataset}
                        onCheckedChange={(checked: boolean) => {
                          setUseSeparateExecutionDataset(checked);
                          if (!checked) {
                            setExecutionDatasetId('');
                            setValidationErrors(prev => {
                              const { executionDataset, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      <Label htmlFor="useSeparateExecution" className="cursor-pointer">
                        Use separate execution dataset
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            If enabled, use a different dataset for trade execution. If disabled, the signal dataset will be used for both signal generation and trade execution.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {useSeparateExecutionDataset ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="executionDataset">Execution Dataset</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Dataset used for executing trades. The timeframe of this dataset determines trade execution precision. 
                                Lower timeframes (e.g., 1min, 5min) provide more precise entry/exit points. 
                                The execution timeframe should be equal or lower than the signal timeframe.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Select 
                          value={executionDatasetId} 
                          onValueChange={(v) => {
                            setExecutionDatasetId(v);
                            setValidationErrors(prev => {
                              const { executionDataset, ...rest } = prev;
                              return rest;
                            });
                          }}
                        >
                          <SelectTrigger id="executionDataset" className={validationErrors.executionDataset ? 'border-red-500' : ''}>
                            <SelectValue placeholder="Select execution dataset" />
                          </SelectTrigger>
                          <SelectContent>
                            {readyDatasets
                              .filter(dataset => {
                                // Filter: execution timeframe should be <= signal timeframe
                                if (!signalDataset || !signalDataset.timeframe || !dataset.timeframe) return true;
                                const signalSeconds = timeframeToSeconds(signalDataset.timeframe);
                                const executionSeconds = timeframeToSeconds(dataset.timeframe);
                                return executionSeconds <= signalSeconds;
                              })
                              .map((dataset) => (
                                <SelectItem key={dataset.id} value={dataset.id}>
                                  {dataset.name} {dataset.timeframe && `(${dataset.timeframe})`}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {validationErrors.executionDataset && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {validationErrors.executionDataset}
                          </p>
                        )}
                        {executionDataset && executionDataset.timeframe && (
                          <div className="p-2 bg-muted rounded-md">
                            <p className="text-xs text-muted-foreground">
                              <strong>Timeframe:</strong> {executionDataset.timeframe}
                            </p>
                            {executionDataset.symbol && (
                              <p className="text-xs text-muted-foreground">
                                <strong>Symbol:</strong> {executionDataset.symbol}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-2 bg-muted rounded-md">
                        <p className="text-xs text-muted-foreground">
                          Using signal dataset for execution: <strong>{signalDataset?.name || 'N/A'}</strong>
                          {signalDataset?.timeframe && ` (${signalDataset.timeframe})`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {signalDataset && executionDataset && signalDataset.timeframe && executionDataset.timeframe && 
                 timeframeToSeconds(executionDataset.timeframe) > timeframeToSeconds(signalDataset.timeframe) && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Execution dataset timeframe ({executionDataset.timeframe}) should be equal or lower than signal dataset timeframe ({signalDataset.timeframe})
                    </p>
                  </div>
                )}
              </div>
              {validationErrors.entrySignals && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {validationErrors.entrySignals}
                  </p>
                </div>
              )}
              {validationErrors.invalidSignals && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {validationErrors.invalidSignals}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Signals Tab */}
          <TabsContent value="signals" className="space-y-4 mt-4">
            <div className="space-y-6">
              {/* Available Signals */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-muted-foreground" />
                  <Label>Available Signals</Label>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  {availableSignals.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {availableSignals.map((signal) => (
                        <span
                          key={signal}
                          className="px-2 py-1 bg-background border rounded text-sm"
                        >
                          {signal}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No signals detected. Make sure your DSL code has variables marked with the <code className="bg-background px-1 rounded">signal</code> keyword.
                    </p>
                  )}
                </div>
              </div>

              {/* Entry Signals */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <Label>Entry Signals</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which signals open positions
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ArrowUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <Label className="text-sm">Long Entries</Label>
                    </div>
                    <div className="space-y-2">
                      {entryLongSignals.map((signal, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Select
                            value={signal}
                            onValueChange={(v) => {
                              const newSignals = [...entryLongSignals];
                              newSignals[idx] = v;
                              setEntryLongSignals(newSignals);
                              // Clear entrySignals error if we now have at least one valid entry signal
                              const hasValidLong = newSignals.filter(Boolean).length > 0;
                              const hasValidShort = entryShortSignals.filter(Boolean).length > 0;
                              if (hasValidLong || hasValidShort) {
                                setValidationErrors(prev => {
                                  const { entrySignals, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableSignalsForSelection([
                                ...entryLongSignals.filter((_, i) => i !== idx),
                                ...entryShortSignals,
                                ...exitLongSignals,
                                ...exitShortSignals,
                              ]).map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                              {/* Include current signal if it's not in available signals list */}
                              {entryLongSignals[idx] && !getAvailableSignalsForSelection([
                                ...entryLongSignals.filter((_, i) => i !== idx),
                                ...entryShortSignals,
                                ...exitLongSignals,
                                ...exitShortSignals,
                              ]).includes(entryLongSignals[idx]) && availableSignals.includes(entryLongSignals[idx]) && (
                                <SelectItem value={entryLongSignals[idx]}>
                                  {entryLongSignals[idx]}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newSignals = entryLongSignals.filter((_, i) => i !== idx);
                              setEntryLongSignals(newSignals);
                              // Clear entrySignals error if we still have at least one entry signal
                              if (newSignals.filter(Boolean).length > 0 || entryShortSignals.filter(Boolean).length > 0) {
                                setValidationErrors(prev => {
                                  const { entrySignals, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEntryLongSignals([...entryLongSignals, '']);
                          // Clear entrySignals error when adding a new signal slot
                          setValidationErrors(prev => {
                            const { entrySignals, ...rest } = prev;
                            return rest;
                          });
                        }}
                        disabled={availableSignals.length === 0}
                      >
                        + Add Signal
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ArrowDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <Label className="text-sm">Short Entries</Label>
                    </div>
                    <div className="space-y-2">
                      {entryShortSignals.map((signal, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Select
                            value={signal}
                            onValueChange={(v) => {
                              const newSignals = [...entryShortSignals];
                              newSignals[idx] = v;
                              setEntryShortSignals(newSignals);
                              // Clear entrySignals error if we now have at least one valid entry signal
                              const hasValidLong = entryLongSignals.filter(Boolean).length > 0;
                              const hasValidShort = newSignals.filter(Boolean).length > 0;
                              if (hasValidLong || hasValidShort) {
                                setValidationErrors(prev => {
                                  const { entrySignals, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals.filter((_, i) => i !== idx),
                                ...exitLongSignals,
                                ...exitShortSignals,
                              ]).map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                              {/* Include current signal if it's not in available signals list */}
                              {entryShortSignals[idx] && !getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals.filter((_, i) => i !== idx),
                                ...exitLongSignals,
                                ...exitShortSignals,
                              ]).includes(entryShortSignals[idx]) && availableSignals.includes(entryShortSignals[idx]) && (
                                <SelectItem value={entryShortSignals[idx]}>
                                  {entryShortSignals[idx]}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newSignals = entryShortSignals.filter((_, i) => i !== idx);
                              setEntryShortSignals(newSignals);
                              // Clear entrySignals error if we still have at least one entry signal
                              if (entryLongSignals.filter(Boolean).length > 0 || newSignals.filter(Boolean).length > 0) {
                                setValidationErrors(prev => {
                                  const { entrySignals, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEntryShortSignals([...entryShortSignals, '']);
                          // Clear entrySignals error when adding a new signal slot
                          setValidationErrors(prev => {
                            const { entrySignals, ...rest } = prev;
                            return rest;
                          });
                        }}
                        disabled={availableSignals.length === 0}
                      >
                        + Add Signal
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exit Signals */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-muted-foreground" />
                    <Label>Exit Signals</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which signals close positions
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ArrowUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <Label className="text-sm">Long Exits</Label>
                    </div>
                    <div className="space-y-2">
                      {exitLongSignals.map((signal, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Select
                            value={signal}
                            onValueChange={(v) => {
                              const newSignals = [...exitLongSignals];
                              newSignals[idx] = v;
                              setExitLongSignals(newSignals);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals,
                                ...exitLongSignals.filter((_, i) => i !== idx),
                                ...exitShortSignals,
                              ]).map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                              {/* Include current signal if it's not in available signals list */}
                              {exitLongSignals[idx] && !getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals,
                                ...exitLongSignals.filter((_, i) => i !== idx),
                                ...exitShortSignals,
                              ]).includes(exitLongSignals[idx]) && availableSignals.includes(exitLongSignals[idx]) && (
                                <SelectItem value={exitLongSignals[idx]}>
                                  {exitLongSignals[idx]}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setExitLongSignals(exitLongSignals.filter((_, i) => i !== idx));
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExitLongSignals([...exitLongSignals, ''])}
                        disabled={availableSignals.length === 0}
                      >
                        + Add Signal
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ArrowDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <Label className="text-sm">Short Exits</Label>
                    </div>
                    <div className="space-y-2">
                      {exitShortSignals.map((signal, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Select
                            value={signal}
                            onValueChange={(v) => {
                              const newSignals = [...exitShortSignals];
                              newSignals[idx] = v;
                              setExitShortSignals(newSignals);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals,
                                ...exitLongSignals,
                                ...exitShortSignals.filter((_, i) => i !== idx),
                              ]).map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                              {/* Include current signal if it's not in available signals list */}
                              {exitShortSignals[idx] && !getAvailableSignalsForSelection([
                                ...entryLongSignals,
                                ...entryShortSignals,
                                ...exitLongSignals,
                                ...exitShortSignals.filter((_, i) => i !== idx),
                              ]).includes(exitShortSignals[idx]) && availableSignals.includes(exitShortSignals[idx]) && (
                                <SelectItem value={exitShortSignals[idx]}>
                                  {exitShortSignals[idx]}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setExitShortSignals(exitShortSignals.filter((_, i) => i !== idx));
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExitShortSignals([...exitShortSignals, ''])}
                        disabled={availableSignals.length === 0}
                      >
                        + Add Signal
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stop Loss / Take Profit */}
              <div className="space-y-4 border-t pt-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    <Label>Stop Loss & Take Profit</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Configure how SL/TP levels are determined (separate for Long and Short)
                  </p>
                </div>

                {/* Long Positions */}
                <div className="space-y-4 border rounded-lg p-4">
                  <Label className="text-base font-semibold">Long Positions</Label>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Stop Loss - Long */}
                    <div className="space-y-3">
                      <Label className="text-sm">Stop Loss</Label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="sl-long-signal"
                          checked={stopLossLongUseSignal}
                          onChange={() => setStopLossLongUseSignal(true)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="sl-long-signal" className="font-normal cursor-pointer">
                          Use signal level
                        </Label>
                      </div>
                      {stopLossLongUseSignal && (
                        <div className="space-y-2">
                          <Select 
                            value={stopLossLongSignalName} 
                            onValueChange={(v) => {
                              setStopLossLongSignalName(v);
                              if (!availableSignals.includes(v)) {
                                setValidationErrors(prev => ({ ...prev, stopLossLongSignal: 'Signal not found in DSL' }));
                              } else {
                                setValidationErrors(prev => {
                                  const { stopLossLongSignal, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            <SelectTrigger className={validationErrors.stopLossLongSignal ? 'border-red-500' : ''}>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSignals.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {validationErrors.stopLossLongSignal && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {validationErrors.stopLossLongSignal}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="sl-long-default"
                          checked={!stopLossLongUseSignal}
                          onChange={() => setStopLossLongUseSignal(false)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="sl-long-default" className="font-normal cursor-pointer">
                          Calculate default
                        </Label>
                      </div>
                      {!stopLossLongUseSignal && (
                        <div className="space-y-2 pl-6">
                          <div>
                            <Label className="text-xs">Multiplier</Label>
                            <Input
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={stopLossLongMultiplier}
                              onChange={(e) => setStopLossLongMultiplier(Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Based on</Label>
                            <Select value={stopLossLongBasedOn} onValueChange={(v: any) => setStopLossLongBasedOn(v)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="atr">ATR</SelectItem>
                                <SelectItem value="price">Price</SelectItem>
                                <SelectItem value="percent">Percentage</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Take Profit - Long */}
                  <div className="space-y-3">
                    <Label className="text-sm">Take Profit</Label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="tp-long-signal"
                          checked={takeProfitLongUseSignal}
                          onChange={() => setTakeProfitLongUseSignal(true)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="tp-long-signal" className="font-normal cursor-pointer">
                          Use signal level
                        </Label>
                      </div>
                      {takeProfitLongUseSignal && (
                        <div className="space-y-2">
                          <Select 
                            value={takeProfitLongSignalName} 
                            onValueChange={(v) => {
                              setTakeProfitLongSignalName(v);
                              if (!availableSignals.includes(v)) {
                                setValidationErrors(prev => ({ ...prev, takeProfitLongSignal: 'Signal not found in DSL' }));
                              } else {
                                setValidationErrors(prev => {
                                  const { takeProfitLongSignal, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          >
                            <SelectTrigger className={validationErrors.takeProfitLongSignal ? 'border-red-500' : ''}>
                              <SelectValue placeholder="Select signal" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSignals.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {validationErrors.takeProfitLongSignal && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {validationErrors.takeProfitLongSignal}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="tp-long-default"
                          checked={!takeProfitLongUseSignal}
                          onChange={() => setTakeProfitLongUseSignal(false)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="tp-long-default" className="font-normal cursor-pointer">
                          Calculate default
                        </Label>
                      </div>
                      {!takeProfitLongUseSignal && (
                        <div className="space-y-2 pl-6">
                          <div>
                            <Label className="text-xs">Multiplier</Label>
                            <Input
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={takeProfitLongMultiplier}
                              onChange={(e) => setTakeProfitLongMultiplier(Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Based on</Label>
                            <Select value={takeProfitLongBasedOn} onValueChange={(v: any) => setTakeProfitLongBasedOn(v)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="atr">ATR</SelectItem>
                                <SelectItem value="price">Price</SelectItem>
                                <SelectItem value="percent">Percentage</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </div>

                {/* Short Positions */}
                <div className="space-y-4 border rounded-lg p-4">
                  <Label className="text-base font-semibold">Short Positions</Label>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Stop Loss - Short */}
                    <div className="space-y-3">
                      <Label className="text-sm">Stop Loss</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="sl-short-signal"
                            checked={stopLossShortUseSignal}
                            onChange={() => setStopLossShortUseSignal(true)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="sl-short-signal" className="font-normal cursor-pointer">
                            Use signal level
                          </Label>
                        </div>
                        {stopLossShortUseSignal && (
                          <div className="space-y-2">
                            <Select 
                              value={stopLossShortSignalName} 
                              onValueChange={(v) => {
                                setStopLossShortSignalName(v);
                                if (!availableSignals.includes(v)) {
                                  setValidationErrors(prev => ({ ...prev, stopLossShortSignal: 'Signal not found in DSL' }));
                                } else {
                                  setValidationErrors(prev => {
                                    const { stopLossShortSignal, ...rest } = prev;
                                    return rest;
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className={validationErrors.stopLossShortSignal ? 'border-red-500' : ''}>
                                <SelectValue placeholder="Select signal" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSignals.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {validationErrors.stopLossShortSignal && (
                              <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {validationErrors.stopLossShortSignal}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="sl-short-default"
                            checked={!stopLossShortUseSignal}
                            onChange={() => setStopLossShortUseSignal(false)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="sl-short-default" className="font-normal cursor-pointer">
                            Calculate default
                          </Label>
                        </div>
                        {!stopLossShortUseSignal && (
                          <div className="space-y-2 pl-6">
                            <div>
                              <Label className="text-xs">Multiplier</Label>
                              <Input
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={stopLossShortMultiplier}
                                onChange={(e) => setStopLossShortMultiplier(Number(e.target.value))}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Based on</Label>
                              <Select value={stopLossShortBasedOn} onValueChange={(v: any) => setStopLossShortBasedOn(v)}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="atr">ATR</SelectItem>
                                  <SelectItem value="price">Price</SelectItem>
                                  <SelectItem value="percent">Percentage</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Take Profit - Short */}
                    <div className="space-y-3">
                      <Label className="text-sm">Take Profit</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="tp-short-signal"
                            checked={takeProfitShortUseSignal}
                            onChange={() => setTakeProfitShortUseSignal(true)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="tp-short-signal" className="font-normal cursor-pointer">
                            Use signal level
                          </Label>
                        </div>
                        {takeProfitShortUseSignal && (
                          <div className="space-y-2">
                            <Select 
                              value={takeProfitShortSignalName} 
                              onValueChange={(v) => {
                                setTakeProfitShortSignalName(v);
                                if (!availableSignals.includes(v)) {
                                  setValidationErrors(prev => ({ ...prev, takeProfitShortSignal: 'Signal not found in DSL' }));
                                } else {
                                  setValidationErrors(prev => {
                                    const { takeProfitShortSignal, ...rest } = prev;
                                    return rest;
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className={validationErrors.takeProfitShortSignal ? 'border-red-500' : ''}>
                                <SelectValue placeholder="Select signal" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSignals.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {validationErrors.takeProfitShortSignal && (
                              <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {validationErrors.takeProfitShortSignal}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="tp-short-default"
                            checked={!takeProfitShortUseSignal}
                            onChange={() => setTakeProfitShortUseSignal(false)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="tp-short-default" className="font-normal cursor-pointer">
                            Calculate default
                          </Label>
                        </div>
                        {!takeProfitShortUseSignal && (
                          <div className="space-y-2 pl-6">
                            <div>
                              <Label className="text-xs">Multiplier</Label>
                              <Input
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={takeProfitShortMultiplier}
                                onChange={(e) => setTakeProfitShortMultiplier(Number(e.target.value))}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Based on</Label>
                              <Select value={takeProfitShortBasedOn} onValueChange={(v: any) => setTakeProfitShortBasedOn(v)}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="atr">ATR</SelectItem>
                                  <SelectItem value="price">Price</SelectItem>
                                  <SelectItem value="percent">Percentage</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Position Management */}
              <div className="space-y-4 border-t pt-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-muted-foreground" />
                    <Label>Position Management</Label>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Max Positions</Label>
                    <Input
                      type="number"
                      min="1"
                      value={maxPositions}
                      onChange={(e) => setMaxPositions(Number(e.target.value))}
                      disabled={!allowMultipleEntries}
                      className={!allowMultipleEntries ? "opacity-50 cursor-not-allowed" : ""}
                    />
                    {!allowMultipleEntries && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Enable "Allow multiple entries" to configure max positions
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="allowMultipleEntries"
                      checked={allowMultipleEntries}
                      onChange={(e) => setAllowMultipleEntries(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="allowMultipleEntries" className="font-normal cursor-pointer">
                        Allow multiple entries (pyramiding)
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            When enabled, allows opening multiple positions in the same direction (pyramiding). 
                            For example, if you have a LONG position open and another LONG entry signal appears, 
                            a new LONG position will be opened. When disabled, only one position per direction 
                            can be open at a time (limited by Max Positions).
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="closeOnOppositeSignal"
                      checked={closeOnOppositeSignal}
                      onChange={(e) => setCloseOnOppositeSignal(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="closeOnOppositeSignal" className="font-normal cursor-pointer">
                        Close on opposite signal
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            When enabled, automatically closes existing positions when an opposite entry signal appears.
                            For example, if you have a LONG position open and a SHORT entry signal appears,
                            the LONG position will be closed automatically. This prevents holding opposite positions
                            simultaneously and ensures your strategy follows the latest market direction.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="closeEndOfDay"
                      checked={closeEndOfDay}
                      onChange={(e) => setCloseEndOfDay(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="closeEndOfDay" className="font-normal cursor-pointer">
                        Close end of day
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            When enabled, automatically closes all open positions at the end of each trading day.
                            This is essential for futures traders where positions cannot be held overnight.
                            Positions are closed at the last candle price of each trading day.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="leverage">Leverage (optional)</Label>
                    <Input
                      id="leverage"
                      type="number"
                      min="1"
                      step="0.1"
                      placeholder="Unlimited"
                      value={leverage || ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        setLeverage(value);
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leverage multiplier for margin trading (e.g., 10 for 10x leverage). Leave empty for unlimited leverage.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="marginRequirement">Margin Requirement % (optional)</Label>
                    <Input
                      id="marginRequirement"
                      type="number"
                      min="0.1"
                      max="100"
                      step="0.1"
                      placeholder="No margin restrictions"
                      value={marginRequirement || ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        setMarginRequirement(value);
                      }}
                    />
                    {validationErrors.marginRequirement && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.marginRequirement}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Margin requirement as percentage of position value (e.g., 5 for 5% margin). Leave empty for spot trading.
                    </p>
                  </div>

                  {validationErrors.leverage && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Leverage: {validationErrors.leverage}
                      </p>
                    </div>
                  )}

                  {validationErrors.marginRequirement && !validationErrors.leverage && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Margin Requirement: {validationErrors.marginRequirement}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-4 mt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Advanced configurations (commissions, slippage, trailing stops, etc.) will be implemented in future phases.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              {t('scripts.reset') || 'Reset'}
            </Button>
            <Button
              variant="outline"
              onClick={handleLoadFromConfigBlock}
              disabled={!integratedConfig?.backtest || isLoadingIntegratedConfig}
              className="min-w-[180px]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('scripts.loadFromConfigBlock') || 'Load from Config Block'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  disabled={isLoadingTemplates}
                  className="min-w-[160px]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isLoadingTemplates ? t('common.loading') || 'Loading...' : selectedTemplateId ? t('scripts.changeTemplate') || 'Change Template' : t('scripts.loadTemplate') || 'Load Template'}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {templates.length === 0 ? (
                  <DropdownMenuItem disabled>
                    <span className="text-muted-foreground">{t('scripts.noTemplatesAvailable') || 'No templates available'}</span>
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedTemplateId('');
                      }}
                    >
                      <span>{t('scripts.noneUseIntegratedCustom') || 'None (use integrated/custom config)'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {templates.map((template) => (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => {
                          setSelectedTemplateId(template.id);
                        }}
                      >
                        <span>{template.name} {template.scriptId && `(${t('scripts.scriptSpecific') || 'Script-specific'})`}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  disabled={isSaving || isLoading || Object.keys(validationErrors).length > 0}
                  className="min-w-[140px]"
                >
                  {isSaving ? (
                    t('common.saving') || 'Saving...'
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('common.save') || 'Save'}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={handleSave}
                  disabled={isSaving || isLoading || Object.keys(validationErrors).length > 0}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveCustomConfiguration') || 'Save Custom Configuration'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('scripts.default') || 'Default'}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {selectedTemplateId && (
                  <>
                    <DropdownMenuItem 
                      onClick={handleUpdateCurrentTemplate}
                      disabled={isSaving || isLoading || Object.keys(validationErrors).length > 0}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      <span>{t('scripts.updateCurrentTemplate') || 'Update Current Template'}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {templates.find(t => t.id === selectedTemplateId)?.name || t('scripts.template') || 'Template'}
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={handleSaveAsTemplate}
                  disabled={isSaving || isLoading || Object.keys(validationErrors).length > 0}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveAsTemplate') || 'Save as Template'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('scripts.reusable') || 'Reusable'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSaveAsYAML}
                  disabled={isSaving || isLoading || Object.keys(validationErrors).length > 0}
                >
                  <Code className="w-4 h-4 mr-2" />
                  <span>{t('scripts.saveAndUpdateScriptConfigBlock') || 'Save & Update Script Config Block'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t('scripts.code') || 'CODE'}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Save as Template Dialog */}
    <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('scripts.saveAsTemplate') || 'Save as Template'}</DialogTitle>
          <DialogDescription>
            {t('scripts.saveAsTemplateDescription') || 'Create a new template or update an existing one with the current configuration.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Action Selection */}
          <div className="space-y-3">
            <Label>{t('scripts.action') || 'Action'}</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="action-create"
                  name="templateAction"
                  value="create"
                  checked={templateAction === 'create'}
                  onChange={() => {
                    setTemplateAction('create');
                    setSelectedTemplateToUpdate('');
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="action-create" className="font-normal cursor-pointer">
                  {t('scripts.createNewTemplate') || 'Create new template'}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="action-update"
                  name="templateAction"
                  value="update"
                  checked={templateAction === 'update'}
                  onChange={() => {
                    setTemplateAction('update');
                    setNewTemplateName('');
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="action-update" className="font-normal cursor-pointer">
                  {t('scripts.updateExistingTemplate') || 'Update existing template'}
                </Label>
              </div>
            </div>
          </div>

          {/* Create new template */}
          {templateAction === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="newTemplateName">{t('scripts.templateName') || 'Template Name'}</Label>
              <Input
                id="newTemplateName"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder={t('scripts.enterTemplateName') || 'Enter template name'}
                autoFocus
              />
            </div>
          )}

          {/* Update existing template */}
          {templateAction === 'update' && (
            <div className="space-y-2">
              <Label htmlFor="templateToUpdate">{t('scripts.selectTemplate') || 'Select Template'}</Label>
              <Select
                value={selectedTemplateToUpdate}
                onValueChange={setSelectedTemplateToUpdate}
              >
                <SelectTrigger id="templateToUpdate">
                  <SelectValue placeholder={t('scripts.selectTemplateToUpdate') || 'Select a template to update'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 ? (
                    <SelectItem value="__empty__" disabled>
                      {t('scripts.noTemplatesAvailable') || 'No templates available'}
                    </SelectItem>
                  ) : (
                    templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} {template.scriptId && `(${t('scripts.scriptSpecific') || 'Script-specific'})`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('scripts.noTemplatesFound') || 'No templates found. Create a new template instead.'}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setSaveTemplateDialogOpen(false);
              setNewTemplateName('');
              setSelectedTemplateToUpdate('');
            }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleTemplateSave}
            disabled={
              (templateAction === 'create' && !newTemplateName.trim()) ||
              (templateAction === 'update' && !selectedTemplateToUpdate)
            }
          >
            {templateAction === 'create' ? t('scripts.create') || 'Create' : t('scripts.update') || 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}
